import {
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { Trash2, Upload } from 'lucide-react'
import React, { useCallback, useRef, useState } from 'react'

import type { ToastLike } from '@mangovoice/mango-ui'
import { ConfirmDialog, Field, Modal } from '@mangovoice/mango-ui'

import type {
  AudioTrackSummary,
  MusicOnHoldApi,
} from '../api/musicOnHoldApi.types'
import {
  useCreateAudioTrack,
  useDeleteAudioTrack,
  useGetAudioTracks,
} from '../queries/musicOnHoldQueries'
import { type MusicOnHoldTranslateFn, text } from '../types/i18n.types'
import { TrackPlayButton } from './TrackPlayButton'

const MAX_AUDIO_FILE_SIZE_BYTES = 25 * 1024 * 1024 // 25 MB, matches mango-api cap

function isValidAudioFile(file: File): boolean {
  const validTypes = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3']
  // The extension fallback is NOT redundant: file.type is empty on some
  // OS/browser combos (notably .wav on Windows), so we must also match by name.
  return validTypes.includes(file.type) || /\.(wav|mp3)$/i.test(file.name)
}

function stripExtension(filename: string): string {
  return filename.replace(/\.[^/.]+$/, '')
}

export interface ManageAudioTracksModalProps {
  api: MusicOnHoldApi
  pbxId: number
  open: boolean
  onClose: () => void
  t?: MusicOnHoldTranslateFn
  toast?: ToastLike
}

export const ManageAudioTracksModal: React.FC<ManageAudioTracksModalProps> = ({
  api,
  pbxId,
  open,
  onClose,
  t,
  toast,
}) => {
  const { data: tracks = [], isLoading, error } = useGetAudioTracks(api, pbxId)
  const createMutation = useCreateAudioTrack(api, pbxId)
  const deleteMutation = useDeleteAudioTrack(api, pbxId)

  // Upload state — dropzone + name field shown inline when the user opts in.
  const [uploaderOpen, setUploaderOpen] = useState(false)
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [trackName, setTrackName] = useState('')
  // Whether the user has manually touched the name field. While false, the
  // name auto-fills from the selected filename; once the user edits it (incl.
  // clearing it), we stop overwriting their input.
  const [nameEdited, setNameEdited] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [isDragOver, setIsDragOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Delete confirmation state.
  const [trackToDelete, setTrackToDelete] = useState<AudioTrackSummary | null>(
    null,
  )

  const resetUploader = useCallback(() => {
    setUploaderOpen(false)
    setAudioFile(null)
    setTrackName('')
    setNameEdited(false)
    setUploadError(null)
    setIsDragOver(false)
  }, [])

  const acceptFile = useCallback(
    (file: File) => {
      setUploadError(null)
      if (!isValidAudioFile(file)) {
        setUploadError(
          text(
            t,
            'musicOnHold.audioTracks.fileTypeInvalid',
            'Only WAV or MP3 files are supported.',
          ),
        )
        return
      }
      if (file.size > MAX_AUDIO_FILE_SIZE_BYTES) {
        const mb = (file.size / 1024 / 1024).toFixed(1)
        setUploadError(
          text(
            t,
            'musicOnHold.audioTracks.fileSizeExceeded',
            'File exceeds 25 MB limit ({{size}} MB).',
          ).replace('{{size}}', mb),
        )
        return
      }
      setAudioFile(file)
      // Default the track name to the filename without the extension, unless the
      // user has already edited the field themselves.
      if (!nameEdited) {
        setTrackName(stripExtension(file.name))
      }
    },
    [t, nameEdited],
  )

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      // Reset the input's value so picking the same file twice (e.g. after a
      // failed validation or a Remove click) still fires onChange.
      e.target.value = ''
      if (file) acceptFile(file)
    },
    [acceptFile],
  )

  const handleFileDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragOver(false)
      const file = e.dataTransfer.files[0]
      if (file) acceptFile(file)
    },
    [acceptFile],
  )

  const handleUpload = useCallback(async () => {
    setUploadError(null)
    const trimmedName = trackName.trim()
    if (!trimmedName) {
      setUploadError(
        text(
          t,
          'musicOnHold.audioTracks.nameRequired',
          'Please enter a name for the track.',
        ),
      )
      return
    }
    if (!audioFile) {
      setUploadError(
        text(
          t,
          'musicOnHold.audioTracks.audioFileRequired',
          'Please select an audio file.',
        ),
      )
      return
    }

    try {
      await createMutation.mutateAsync({
        name: trimmedName,
        file: audioFile,
        pbx_id: pbxId,
      })
      resetUploader()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast?.create({
        title: text(t, 'musicOnHold.audioTracks.uploadFailed', 'Upload failed'),
        description: message,
        type: 'error',
      })
    }
  }, [audioFile, createMutation, pbxId, resetUploader, t, toast, trackName])

  const handleDeleteConfirm = useCallback(async () => {
    if (!trackToDelete?.uuid) return
    try {
      await deleteMutation.mutateAsync(trackToDelete.uuid)
      setTrackToDelete(null)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      toast?.create({
        title: text(t, 'musicOnHold.audioTracks.deleteFailed', 'Delete failed'),
        description: message,
        type: 'error',
      })
    }
  }, [deleteMutation, t, toast, trackToDelete])

  const handleClose = () => {
    // Don't let the dialog close out from under an in-flight upload
    // (mirrors the delete confirm dialog's pending guard).
    if (createMutation.isPending) return
    resetUploader()
    onClose()
  }

  return (
    <>
      <Modal
        open={open}
        onOpenChange={(e) => {
          if (!e.open) handleClose()
        }}
        closeDisabled={createMutation.isPending}
        title={text(t, 'musicOnHold.audioTracks.title', 'Audio Tracks')}
        maxWidth="2xl"
        footer={
          <Button
            borderRadius={'lg'}
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            {text(t, 'musicOnHold.audioTracks.close', 'Close')}
          </Button>
        }
      >
        {!uploaderOpen && (
          <HStack justifyContent="flex-end" mb={4}>
            <Button
              size="sm"
              variant="outline"
              flexShrink={0}
              onClick={() => setUploaderOpen(true)}
            >
              <Icon asChild mr={1}>
                <Upload size={14} />
              </Icon>
              {text(t, 'musicOnHold.audioTracks.uploadButton', 'Upload Music')}
            </Button>
          </HStack>
        )}
        {uploaderOpen && (
          <Box
            mb={4}
            p={4}
            borderWidth="1px"
            borderColor="border.default"
            borderRadius="md"
          >
            <VStack gap={3} align="stretch">
              <Box
                border="2px dashed"
                borderColor={
                  isDragOver
                    ? 'border.emphasized'
                    : uploadError
                      ? 'border.error'
                      : 'border.default'
                }
                borderRadius="lg"
                p={6}
                textAlign="center"
                cursor="pointer"
                bg={isDragOver ? 'bg.muted' : 'bg.subtle'}
                transition="all 0.15s"
                _hover={{
                  borderColor: 'border.emphasized',
                  bg: 'bg.muted',
                }}
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e: React.DragEvent) => {
                  e.preventDefault()
                  setIsDragOver(true)
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleFileDrop}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/wav,audio/x-wav,audio/mpeg,.wav,.mp3"
                  onChange={handleFileSelect}
                  style={{ display: 'none' }}
                />
                {audioFile ? (
                  <VStack gap={1}>
                    <Icon asChild boxSize={6} color="brand.solid">
                      <Upload size={24} />
                    </Icon>
                    <Text fontWeight="medium">{audioFile.name}</Text>
                    <Text fontSize="sm" color="fg.muted">
                      {(audioFile.size / (1024 * 1024)).toFixed(2)} MB
                    </Text>
                    <Button
                      size="xs"
                      variant="ghost"
                      borderRadius="lg"
                      colorPalette="error"
                      onClick={(e) => {
                        e.stopPropagation()
                        setAudioFile(null)
                      }}
                    >
                      {text(t, 'musicOnHold.audioTracks.removeFile', 'Remove')}
                    </Button>
                  </VStack>
                ) : (
                  <VStack gap={1}>
                    <Icon asChild boxSize={8} color="fg.muted">
                      <Upload size={32} />
                    </Icon>
                    <Text>
                      {text(
                        t,
                        'musicOnHold.audioTracks.dropzonePlaceholder',
                        'Drag & drop a WAV or MP3 file here, or click to browse',
                      )}
                    </Text>
                    <Text fontSize="sm" color="fg.muted">
                      {text(
                        t,
                        'musicOnHold.audioTracks.dropzoneAccepted',
                        'WAV, MP3 (max 25 MB)',
                      )}
                    </Text>
                  </VStack>
                )}
              </Box>

              <Field.Root required>
                <Field.Label>
                  {text(t, 'musicOnHold.audioTracks.nameLabel', 'Track Name')}
                </Field.Label>
                <Input
                  value={trackName}
                  onChange={(e) => {
                    setNameEdited(true)
                    setTrackName(e.target.value)
                  }}
                  placeholder={text(
                    t,
                    'musicOnHold.audioTracks.namePlaceholder',
                    'Enter a track name',
                  )}
                  // Matches mango-api's AudioTrack.name CharField(max_length=255)
                  // so users see the cap enforced at the input instead
                  // of discovering it via a 400 after upload.
                  maxLength={255}
                />
              </Field.Root>

              {uploadError && (
                <Text color="fg.error" fontSize="sm">
                  {uploadError}
                </Text>
              )}

              <HStack gap={2} justify="flex-end">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={resetUploader}
                  disabled={createMutation.isPending}
                >
                  {text(t, 'musicOnHold.audioTracks.cancel', 'Cancel')}
                </Button>
                <Button
                  size="sm"
                  colorPalette="brand"
                  onClick={handleUpload}
                  loading={createMutation.isPending}
                  loadingText={text(
                    t,
                    'musicOnHold.audioTracks.uploading',
                    'Uploading...',
                  )}
                >
                  {text(t, 'musicOnHold.audioTracks.uploadSubmit', 'Upload')}
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}

        {isLoading && (
          <Box textAlign="center" py={6}>
            <Spinner size="lg" />
            <Text mt={2} fontSize="sm" color="fg.muted">
              {text(
                t,
                'musicOnHold.audioTracks.loading',
                'Loading audio tracks...',
              )}
            </Text>
          </Box>
        )}
        {error && (
          <Text color="fg.error" fontSize="sm">
            {(error as Error).message}
          </Text>
        )}
        {!isLoading && !error && tracks.length === 0 && (
          <Box textAlign="center" py={6}>
            <Text color="fg.muted" fontSize="sm">
              {text(
                t,
                'musicOnHold.audioTracks.empty',
                'No audio tracks have been uploaded for this PBX.',
              )}
            </Text>
          </Box>
        )}
        {!isLoading && tracks.length > 0 && (
          <Table.Root variant="outline" size="sm">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader px={3} py={2}>
                  {text(t, 'musicOnHold.audioTracks.columnName', 'Name')}
                </Table.ColumnHeader>
                <Table.ColumnHeader px={3} py={2}>
                  {text(t, 'musicOnHold.audioTracks.columnFile', 'File')}
                </Table.ColumnHeader>
                <Table.ColumnHeader px={3} py={2} />
                <Table.ColumnHeader px={3} py={2} w="60px" />
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {tracks.map((track: AudioTrackSummary) => (
                <Table.Row key={track.id}>
                  <Table.Cell px={3} py={2}>
                    <Text fontWeight="medium" fontSize="sm">
                      {track.name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={3} py={2}>
                    <Text fontSize="sm" color="fg.muted">
                      {track.mp3_file_name}
                    </Text>
                  </Table.Cell>
                  <Table.Cell px={3} py={2}>
                    <TrackPlayButton
                      api={api}
                      pbxId={pbxId}
                      trackUuid={track.uuid}
                      t={t}
                    />
                  </Table.Cell>
                  <Table.Cell px={3} py={2}>
                    <Button
                      size="xs"
                      variant="ghost"
                      colorPalette="error"
                      onClick={() => setTrackToDelete(track)}
                      disabled={!track.uuid}
                      aria-label={text(
                        t,
                        'musicOnHold.audioTracks.deleteAriaLabel',
                        'Delete audio track',
                      )}
                    >
                      <Icon asChild>
                        <Trash2 size={14} />
                      </Icon>
                    </Button>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Modal>

      <ConfirmDialog
        open={trackToDelete !== null}
        onClose={() => {
          if (!deleteMutation.isPending) setTrackToDelete(null)
        }}
        title={text(
          t,
          'musicOnHold.audioTracks.deleteConfirmTitle',
          'Delete audio track?',
        )}
        description={
          trackToDelete
            ? text(
                t,
                'musicOnHold.audioTracks.deleteConfirmBody',
                'Delete "{{name}}"? This cannot be undone.',
              ).replace('{{name}}', trackToDelete.name)
            : ''
        }
        confirmLabel={text(
          t,
          'musicOnHold.audioTracks.deleteConfirm',
          'Delete',
        )}
        cancelLabel={text(t, 'musicOnHold.audioTracks.cancel', 'Cancel')}
        onConfirm={handleDeleteConfirm}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />
    </>
  )
}
